import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranscription } from './TranscriptionContext';

const RecordingContext = createContext();

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const RecordingProvider = ({ children }) => {
  const { transcribe, mode: transcriptionMode } = useTranscription();
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [interimText, setInterimText] = useState('');
  const [speechSupported, setSpeechSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [audioChunks, setAudioChunks] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [confusedMarkers, setConfusedMarkers] = useState([]);
  const [bookmarks, setBookmarks] = useState([]); // {timestamp, seconds, type, label}
  const [lastVoiceCommand, setLastVoiceCommand] = useState(null);
  const [isRecordingDialogOpen, setIsRecordingDialogOpen] = useState(false);
  const [recordingSource, setRecordingSource] = useState('mic'); // 'mic' | 'tab'

  // Refs to avoid stale closure issues in SpeechRecognition callbacks
  const recordingRef = useRef(false);
  const recordingTimeRef = useRef(0);
  const recognitionRef = useRef(null);
  const audioStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Keep refs in sync with state
  useEffect(() => {
    recordingRef.current = recording;
  }, [recording]);

  useEffect(() => {
    recordingTimeRef.current = recordingTime;
  }, [recordingTime]);

  // Check for Web Speech API support on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SpeechRecognition);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const createSpeechRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      setInterimText(interimTranscript);

      if (finalTranscript.trim()) {
        // Use ref for current time — avoids stale closure
        const timestamp = formatTime(recordingTimeRef.current);
        const text = finalTranscript.trim();
        const lower = text.toLowerCase();

        // Voice command detection
        const VOICE_COMMANDS = [
          { patterns: ["i'm confused", "im confused", "i am confused"], type: 'confused', label: "I'm Confused" },
          { patterns: ["highlight this", "highlight that"], type: 'highlight', label: 'Highlight' },
          { patterns: ["important", "this is important", "that's important"], type: 'important', label: 'Important' },
          { patterns: ["new topic", "next topic", "topic change"], type: 'topic', label: 'New Topic' },
        ];

        let isCommand = false;
        for (const cmd of VOICE_COMMANDS) {
          if (cmd.patterns.some(p => lower.includes(p))) {
            const bookmark = {
              timestamp,
              seconds: recordingTimeRef.current,
              type: cmd.type,
              label: cmd.label,
            };
            setBookmarks(prev => [...prev, bookmark]);
            if (cmd.type === 'confused') {
              setConfusedMarkers(prev => [...prev, { timestamp, seconds: recordingTimeRef.current }]);
            }
            setLastVoiceCommand(bookmark);
            // Clear command indicator after 2 seconds
            setTimeout(() => setLastVoiceCommand(null), 2500);
            isCommand = true;
            break;
          }
        }

        setLiveTranscript(prev => [...prev, {
          timestamp,
          text,
          isLive: true,
          voiceCommand: isCommand ? true : undefined,
        }]);
        setInterimText('');
      }
    };

    recognition.onerror = (event) => {
      console.warn('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone permissions.');
      } else if (event.error === 'network') {
        // Network error means browser speech API can't reach Google servers.
        // This is common and not fatal — recording continues, just no live preview.
        console.warn('Web Speech API network error — live preview unavailable. Recording continues.');
        setIsListening(false);
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        // Only show toast for unexpected errors, not common ones
        console.warn(`Speech recognition: ${event.error}`);
      }
    };

    recognition.onend = () => {
      // Use ref to check current recording state — avoids stale closure
      if (recordingRef.current) {
        try {
          recognition.start();
          return; // Don't set isListening to false if restarting
        } catch (e) {
          
        }
      }
      setIsListening(false);
    };

    recognition.onstart = () => {
      setIsListening(true);
    };

    return recognition;
  }, []); // No dependencies — uses refs instead of state

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
          setAudioChunks(prev => [...prev, e.data]);
        }
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start(10000);
      setMediaRecorder(recorder);
      setRecording(true);
      setAudioChunks([]);
      setLiveTranscript([]);
      setInterimText('');
      setRecordingTime(0);
      setConfusedMarkers([]);
      setBookmarks([]);
      setLastVoiceCommand(null);

      // Start Web Speech Recognition for live transcription
      if (speechSupported) {
        const recognition = createSpeechRecognition();
        recognitionRef.current = recognition;
        try {
          recognition.start();
          toast.success('Recording started with live transcription!', { icon: '🎤' });
        } catch (e) {
          console.error('Speech recognition start error:', e);
          toast.success('Recording started!');
        }
      } else {
        toast.success('Recording started! Transcript will be generated when you stop.');
      }

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording. Please allow microphone access.');
    }
  }, [speechSupported, createSpeechRecognition]);

  const stopRecordingRef = useRef(null);

  const startTabCapture = useCallback(async () => {
    try {
      // Request screen/tab sharing with audio
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true, // Required by API, we'll discard the video track
        audio: true, // This captures the tab's audio
      });

      // Check if audio track was shared
      const audioTracks = displayStream.getAudioTracks();
      if (audioTracks.length === 0) {
        displayStream.getTracks().forEach(t => t.stop());
        toast.error('No audio shared. Make sure to check "Share tab audio" in the dialog.');
        return;
      }

      // Stop video tracks — we only need audio
      displayStream.getVideoTracks().forEach(t => t.stop());

      // Create audio-only stream
      const audioStream = new MediaStream(audioTracks);
      audioStreamRef.current = audioStream;

      const recorder = new MediaRecorder(audioStream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
          setAudioChunks(prev => [...prev, e.data]);
        }
      };

      recorder.onstop = () => {
        audioStream.getTracks().forEach(track => track.stop());
      };

      // Stop recording if the user stops sharing the tab
      audioTracks[0].onended = () => {
        if (recordingRef.current && stopRecordingRef.current) {
          stopRecordingRef.current();
          toast.info('Tab sharing ended. Recording stopped.');
        }
      };

      recorder.start(10000);
      setMediaRecorder(recorder);
      setRecording(true);
      setRecordingSource('tab');
      setAudioChunks([]);
      setLiveTranscript([]);
      setInterimText('');
      setRecordingTime(0);
      setConfusedMarkers([]);
      setBookmarks([]);
      setLastVoiceCommand(null);

      toast.success('Capturing tab audio! Play your online lecture now.', { icon: '🔊' });

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.info('Tab sharing cancelled.');
      } else {
        console.error('Tab capture error:', error);
        toast.error('Failed to capture tab audio. Your browser may not support this feature.');
      }
    }
  }, []);

  const stopRecording = useCallback(async () => {
    // Stop speech recognition
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimText('');

    // Stop media recorder
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    setRecording(false);

    // Clear timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    // Process through hybrid transcription (local or cloud based on user tier)
    const chunks = audioChunksRef.current;
    if (chunks.length > 0) {
      setTranscribing(true);
      const modeLabel = transcriptionMode === 'cloud' ? 'Groq Whisper' : 'Local Whisper';
      toast.info(`Processing with ${modeLabel}...`, { icon: '🎯' });

      try {
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
        const finalBlob = new Blob(chunks, { type: mimeType });

        const result = await transcribe(finalBlob);

        if (result && result.success && result.transcript) {
          const whisperText = result.transcript;
          setLiveTranscript([{
            timestamp: '00:00',
            text: whisperText,
            isWhisper: true,
            model: result.model,
          }]);
          toast.success(`Transcript ready via ${result.model}!`, { icon: '✅' });
          return whisperText;
        } else {
          toast.success('Recording complete! Using live transcript.');
          return null;
        }
      } catch (error) {
        console.error('Transcription error:', error);
        toast.info('Using live transcript (AI transcription unavailable)');
        return null;
      } finally {
        setTranscribing(false);
      }
    } else {
      toast.success('Recording stopped!');
      return null;
    }
  }, [mediaRecorder, transcribe, transcriptionMode]);

  // Keep stopRecording ref in sync for tab capture's onended callback
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  const markConfused = useCallback((mode = 'mark') => {
    const currentTimestamp = formatTime(recordingTimeRef.current);

    if (mode === 'mark') {
      setConfusedMarkers(prev => [...prev, {
        timestamp: currentTimestamp,
        seconds: recordingTimeRef.current,
      }]);
      setBookmarks(prev => [...prev, {
        timestamp: currentTimestamp,
        seconds: recordingTimeRef.current,
        type: 'confused',
        label: "I'm Confused",
      }]);
      toast.success(`Marked at ${currentTimestamp} for later review`, { icon: '📍' });
    }
    return currentTimestamp;
  }, []);

  const addBookmark = useCallback((type, label) => {
    const timestamp = formatTime(recordingTimeRef.current);
    const bookmark = { timestamp, seconds: recordingTimeRef.current, type, label: label || type };
    setBookmarks(prev => [...prev, bookmark]);
    setLastVoiceCommand(bookmark);
    setTimeout(() => setLastVoiceCommand(null), 2500);
    toast.success(`${label || type} at ${timestamp}`, { icon: type === 'highlight' ? '🔖' : type === 'important' ? '⭐' : type === 'topic' ? '📌' : '📍' });
    return timestamp;
  }, []);

  // Pause/resume speech recognition (for "I'm Confused" feature)
  const pauseSpeechRecognition = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, [isListening]);

  const resumeSpeechRecognition = useCallback(() => {
    if (recording && speechSupported && !recognitionRef.current) {
      const recognition = createSpeechRecognition();
      recognitionRef.current = recognition;
      try {
        recognition.start();
      } catch (e) {
        console.error('Error resuming speech recognition:', e);
      }
    }
  }, [recording, speechSupported, createSpeechRecognition]);

  const value = {
    // State
    recording,
    transcribing,
    recordingTime,
    liveTranscript,
    interimText,
    speechSupported,
    isListening,
    audioChunks,
    confusedMarkers,
    bookmarks,
    lastVoiceCommand,
    isRecordingDialogOpen,
    recordingSource,
    // Actions
    startRecording,
    startTabCapture,
    stopRecording,
    markConfused,
    addBookmark,
    pauseSpeechRecognition,
    resumeSpeechRecognition,
    setLiveTranscript,
    setIsRecordingDialogOpen,
    // Utilities
    formatTime,
  };

  return (
    <RecordingContext.Provider value={value}>
      {children}
    </RecordingContext.Provider>
  );
};

export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error('useRecording must be used within a RecordingProvider');
  }
  return context;
};
